/**
 * Pure helpers behind a client's flag archive: reading the removal record and the frozen name,
 * category and priority off an inactive `Flag`, and ordering the rows.
 */

import { getExtension, WithId } from "@medplum/core";
import type { Flag, Reference } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

import { ArchivedClientFlag, ClientFlagRemoval } from "../interfaces/client-flags";

import { compareFlagCategories, compareUnsetLast, sortFlagRows } from "./flag-sort";
import { getFlagPriorityRank } from "./flags";
import { getLibraryFlagCoding, toPracticeFlag } from "./practice-flag";

/**
 * Reads the record a removal left on an applied flag: when, by whom, and why.
 *
 * @param flag - An inactive applied `Flag`, as searched from the chart
 * @returns The removal record, with `""` for each field the resource does not carry
 */
export function getClientFlagRemoval(flag: Flag): ClientFlagRemoval {
  const removedBy = getExtension(flag, Extensions.ClientFlagRemovedBy)?.valueReference as
    | Reference
    | undefined;

  return {
    removedAt: flag.period?.end ?? "",
    removedByReference: removedBy?.reference ?? "",
    reason: getExtension(flag, Extensions.ClientFlagRemovalReason)?.valueString ?? "",
  };
}

/**
 * The distinct practitioners named across a set of removals, so their names are read once each.
 *
 * @param removals - Removal records, as `getClientFlagRemoval` read them
 * @returns Each non-empty `removedByReference`, once, in first-seen order
 */
export function collectRemoverReferences(removals: Iterable<ClientFlagRemoval>): string[] {
  const references = new Set<string>();

  for (const { removedByReference } of removals) {
    if (removedByReference) references.add(removedByReference);
  }

  return [...references];
}

/**
 * Builds the archive row for an inactive `Flag` from the values frozen onto it at removal — see
 * `buildRemovedClientFlagResource` — and its removal record. The library is not consulted, so a
 * later library edit or delete leaves the row as it was.
 *
 * @param flag - An inactive applied `Flag`, as searched from the chart
 * @param removerNames - Display names keyed by practitioner reference; a missing name leaves
 *   `removedBy` blank
 * @returns The row, or `undefined` when the flag carries no frozen name — it was removed before
 *   removals froze their values, and there is nothing to show for it
 */
export function toArchivedRow(
  flag: WithId<Flag>,
  removerNames: Map<string, string>,
): ArchivedClientFlag | undefined {
  // Read straight off the coding: `toPracticeFlag` falls back to the code, which an unfrozen flag
  // also carries.
  const name = getLibraryFlagCoding(flag)?.display;

  if (!name) return undefined;

  const { category, priority } = toPracticeFlag(flag);
  const removal = getClientFlagRemoval(flag);

  return {
    id: flag.id,
    name,
    category,
    priority,
    isDeprecated:
      getExtension(flag, Extensions.ClientFlagLibraryEntryDeleted)?.valueBoolean === true,
    removedAt: removal.removedAt,
    reason: removal.reason,
    removedBy: removerNames.get(removal.removedByReference) ?? "",
  };
}

/**
 * Most recently removed first, which is the order the tab opens on. A row with no removal date
 * sorts last.
 */
export function sortByMostRecentlyRemoved(flags: ArchivedClientFlag[]): ArchivedClientFlag[] {
  return [...flags].sort((a, b) => compareUnsetLast(a.removedAt, b.removedAt, -1));
}

export enum ArchiveSortKey {
  Name = "name",
  Priority = "priority",
  Category = "category",
  Date = "date",
  Provider = "provider",
}

/**
 * Reorders archive rows for a sorted column, as `sortFlagRows` reads `sort`. The archive sorts on
 * columns the library has no equivalent for (when a flag was removed, and by whom), so it names its
 * own comparator. A null `sort` returns the input order, most recently removed first.
 */
export function sortArchivedClientFlags(
  flags: ArchivedClientFlag[],
  sort: string | null,
): ArchivedClientFlag[] {
  return sortFlagRows(flags, sort, compareArchivedFlags);
}

function compareArchivedFlags(
  a: ArchivedClientFlag,
  b: ArchivedClientFlag,
  sortKey: ArchiveSortKey,
): number {
  switch (sortKey) {
    case ArchiveSortKey.Priority:
      return getFlagPriorityRank(a.priority) - getFlagPriorityRank(b.priority);
    case ArchiveSortKey.Category:
      return compareFlagCategories(a, b);
    case ArchiveSortKey.Date:
      return compareUnsetLast(a.removedAt, b.removedAt);
    case ArchiveSortKey.Provider:
      return compareUnsetLast(a.removedBy, b.removedBy);
    default:
      return a.name.localeCompare(b.name);
  }
}
